import { AGWASuiteApiClientBase, ApiResponse } from '../agwasuite-api';
import type {
  AdminSitesResult,
  AttachSiteDomainScopeInput,
  AttachSiteDomainScopeResult,
  CreateLoginSiteInput,
  DataTableDataRequest,
  GetSiteDomainScopesResult,
  LoginSiteResult,
  MemberSitesResult,
  SetSiteOwnerInput,
  UpdateLoginSiteInput,
} from '../agwa-api-model';

/**
 * API client for site management operations.
 * Supports both admin-level operations (all sites) and member-level operations (sites per group).
 */
export class ApiSiteMaintenance extends AGWASuiteApiClientBase {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  // --- Admin Site Operations ---

  /**
   * Search all sites in the system with filtering, sorting, and pagination.
   * Requires MemberAdmin rights.
   */
  async searchAllSites(
    request: DataTableDataRequest
  ): Promise<ApiResponse<AdminSitesResult>> {
    return this.postExecute<DataTableDataRequest, AdminSitesResult>(
      '/api/sites/admin/search',
      request
    );
  }

  // --- Member Site Operations ---

  /**
   * Search sites for a specific group with filtering, sorting, and pagination.
   */
  async getMemberSites(
    groupId: string
  ): Promise<ApiResponse<MemberSitesResult>> {
    return this.request<MemberSitesResult>(
      'GET',
      `/api/sites/group/${groupId}/all`
    );
  }

  // --- CRUD Operations ---

  /**
   * Get a single site by ID.
   */
  async getSite(siteId: string): Promise<ApiResponse<LoginSiteResult>> {
    return this.request<LoginSiteResult>('GET', `/api/sites/${siteId}`);
  }

  /**
   * Create a new site for a group.
   */
  async createSite(
    input: CreateLoginSiteInput
  ): Promise<ApiResponse<LoginSiteResult>> {
    return this.postExecute<CreateLoginSiteInput, LoginSiteResult>(
      '/api/sites',
      input
    );
  }

  /**
   * Update an existing site.
   */
  async updateSite(
    siteId: string,
    input: UpdateLoginSiteInput
  ): Promise<ApiResponse<LoginSiteResult>> {
    return this.request<LoginSiteResult>('PUT', `/api/sites/${siteId}`, input);
  }

  /**
   * Deactivate (soft delete) a site.
   */
  async deactivateSite(siteId: string): Promise<ApiResponse<LoginSiteResult>> {
    return this.request<LoginSiteResult>('DELETE', `/api/sites/${siteId}`);
  }

  /**
   * Set or clear a site's owner as a standalone owner-only change - the action behind the site
   * page's read-only "Change Contact" button. A null ownerLoginUserId clears the owner. Changing the
   * owner as part of a full site edit rides createSite/updateSite instead. The server validates that
   * the owner is a person of the site's group who has not been deleted; a blocked person may be
   * chosen (D-174).
   */
  async setSiteOwner(
    siteId: string,
    ownerLoginUserId: string | null
  ): Promise<ApiResponse<LoginSiteResult>> {
    return this.request<LoginSiteResult>('PUT', `/api/sites/${siteId}/owner`, {
      loginSiteId: siteId,
      // Send undefined (omitted) rather than "" so the server's Guid? binds to null and clears.
      ownerLoginUserId: ownerLoginUserId ?? undefined,
    } satisfies SetSiteOwnerInput);
  }

  // --- Site Domain Operations ---

  /**
   * Get all domain scopes attached to a site.
   */
  async getSiteDomains(
    siteId: string
  ): Promise<ApiResponse<GetSiteDomainScopesResult>> {
    return this.request<GetSiteDomainScopesResult>(
      'GET',
      `/api/sites/${siteId}/domains`
    );
  }

  /**
   * Attach a domain scope to a site.
   */
  async attachSiteDomain(
    siteId: string,
    input: AttachSiteDomainScopeInput
  ): Promise<ApiResponse<AttachSiteDomainScopeResult>> {
    return this.postExecute<
      AttachSiteDomainScopeInput,
      AttachSiteDomainScopeResult
    >(`/api/sites/${siteId}/domains`, {
      ...input,
      loginSiteId: siteId,
    });
  }

  /**
   * Deactivate (soft) a site-level domain scope. The site behaves as though the operation type
   * were not attached; its data and history are retained and it can be reactivated later.
   */
  async deactivateSiteDomain(
    siteId: string,
    scopeId: string
  ): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(
      'POST',
      `/api/sites/${siteId}/domains/${scopeId}/disable`,
      {}
    );
  }

  /**
   * Reactivate a previously deactivated site-level domain scope.
   */
  async reactivateSiteDomain(
    siteId: string,
    scopeId: string
  ): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(
      'POST',
      `/api/sites/${siteId}/domains/${scopeId}/reactivate`,
      {}
    );
  }

  /**
   * Hard-delete a site-level domain scope. The server gates this on the scope being a bare header
   * with no recorded data; otherwise the supported action is deactivation.
   */
  async hardDeleteSiteDomain(
    siteId: string,
    scopeId: string
  ): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(
      'DELETE',
      `/api/sites/${siteId}/domains/${scopeId}`
    );
  }
}
